"use client";

import { cn } from "@repo/ui/lib/cn";
import { connectivityStore } from "@saas/shared/lib/connectivity/connectivity-store";
import { useConnectivity } from "@saas/shared/lib/connectivity/use-connectivity";
import { BannerShell } from "@shared/components/BannerShell";
import { useEffect, useRef, useState } from "react";
import { toast } from "@repo/ui/components/sonner";

/** Matches encounter milestone “focused scope” dots (PA Stepper / workspace chrome). */
function StatusDot({
	className,
	pulse,
}: {
	className: string;
	pulse?: boolean;
}) {
	return (
		<span
			aria-hidden
			className={cn(
				"size-2 shrink-0 rounded-full shadow-[0_0_0_2px_rgba(0,0,0,0.06)] dark:shadow-[0_0_0_2px_rgba(255,255,255,0.08)]",
				pulse && "animate-pulse",
				className,
			)}
		/>
	);
}

const RECOVERY_BANNER_MS = 4000;

export function ConnectivityBanner({
	embedded = false,
	className,
}: {
	embedded?: boolean;
	className?: string;
} = {}) {
	const c = useConnectivity();
	const [nowTick, setNowTick] = useState(() => Date.now());
	const [showRecovery, setShowRecovery] = useState(false);
	const prevStatusRef = useRef<typeof c.status | undefined>(undefined);

	useEffect(() => {
		const prev = prevStatusRef.current;
		prevStatusRef.current = c.status;
		if (prev !== undefined && prev !== "online" && c.status === "online") {
			setShowRecovery(true);
			toast.success("Connection restored — syncing your actions now.");
			const t = window.setTimeout(
				() => setShowRecovery(false),
				RECOVERY_BANNER_MS,
			);
			return () => window.clearTimeout(t);
		}
		return undefined;
	}, [c.status]);

	useEffect(() => {
		if (c.status !== "offline" && c.status !== "degraded") {
			return undefined;
		}
		const id = window.setInterval(() => setNowTick(Date.now()), 250);
		return () => window.clearInterval(id);
	}, [c.status]);

	if (c.status === "online" && !showRecovery) {
		return null;
	}

	const recoveryMessage = "Connection restored. Syncing your actions now...";

	const offlineMessage =
		"You're offline. Actions will sync once the connection is re-established.";

	const reconnectingMessage =
		"Reconnecting... Actions will sync once the connection is re-established.";

	const degradedMessage =
		"Connection check failed. We'll keep retrying until the server is reachable.";

	let dotClass: string;
	let pulse = false;
	let message: string;
	let showRetryRow = false;

	if (showRecovery && c.status === "online") {
		dotClass = "bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.25)]";
		message = recoveryMessage;
	} else if (c.status === "offline") {
		dotClass = "bg-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.3)]";
		message = offlineMessage;
		showRetryRow = true;
		pulse = true;
	} else if (c.status === "degraded") {
		dotClass = "bg-amber-500 shadow-[0_0_0_2px_rgba(245,158,11,0.35)]";
		message = degradedMessage;
		showRetryRow = true;
		pulse = true;
	} else {
		// reconnecting
		dotClass = "bg-amber-500 shadow-[0_0_0_2px_rgba(245,158,11,0.35)]";
		message = reconnectingMessage;
		pulse = true;
	}

	const retrySeconds =
		typeof c.nextRetryAtMs === "number"
			? Math.max(0, Math.ceil((c.nextRetryAtMs - nowTick) / 1000))
			: null;

	const content = (
		<div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-sm">
			<div className="flex min-w-0 flex-1 items-center justify-center gap-2 sm:flex-initial sm:justify-start">
				<StatusDot className={dotClass} pulse={pulse} />
				<span
					className={cn(
						"text-center text-gray-900 dark:text-gray-100 sm:text-left",
						c.status === "online" && showRecovery && "font-medium",
					)}
				>
					{message}
				</span>
			</div>
			{showRetryRow ? (
				<div className="flex w-full shrink-0 items-center justify-center gap-3 sm:w-auto sm:justify-end">
					<span className="tabular-nums text-gray-700/70 dark:text-gray-200/70">
						{retrySeconds !== null
							? `Retrying in ${retrySeconds}s…`
							: "Retrying…"}
					</span>
					<button
						type="button"
						className={cn(
							"h-7 shrink-0 rounded-md px-2.5 text-xs font-medium",
							"bg-neutral-950 text-white shadow-sm hover:bg-neutral-900",
							"dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white",
						)}
						onClick={() => connectivityStore.requestReconnectNow()}
					>
						Retry Now
					</button>
				</div>
			) : null}
		</div>
	);

	if (embedded) {
		return <BannerShell className={className}>{content}</BannerShell>;
	}

	return (
		<div className={cn("fixed left-0 right-0 top-16 z-[210]", className)}>
			<BannerShell>{content}</BannerShell>
		</div>
	);
}
